import { useMemo, useState, type ReactNode } from "react";
import { CalendarPlus, ChevronLeft, Mail, MapPin, Phone, UserRound } from "lucide-react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useOutletContext, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { Avatar, AvatarGradientFallback, Button, Card, Eyebrow, Heading, initialsFor, Text } from "@athena/ui";
import { LOST_CONTACT_SUGGESTED_AFTER_INTERVALS } from "@athena/common/utils/clientEngagement";
import { ClientDocument } from "@/graphql/clients.generated";
import { CoordinatorNotesCard } from "./components/CoordinatorNotesCard";
import { LogContactDialog, type LoggedContact } from "./components/LogContactDialog";
import { ScheduleFollowUpDialog, type ScheduledFollowUp } from "./components/ScheduleFollowUpDialog";
import { useClientNotes } from "../../hooks/useClientNotes";
import { useLogContact } from "@/hooks/useLogContact";
import { useScheduleFollowUp } from "@/hooks/useScheduleFollowUp";
import { ContactDetailsCard } from "./components/ContactDetailsCard";
import { ContactHistoryCard } from "./components/ContactHistoryCard";
import { ContactStatusBadge } from "./components/ContactStatusBadge";
import { DoNotContactSelect } from "./components/DoNotContactSelect";
import { MarkLostContact } from "./components/MarkLostContact";
import { MilestoneBadge } from "./components/MilestoneBadge";
import { ResetSoberDate } from "./components/ResetSoberDate";
import { ReturnToOutreach } from "./components/ReturnToOutreach";
import { SendWelcomeEmail } from "./components/SendWelcomeEmail";
import type { ClientRecord, Duration } from "./helpers/client";
import {
    toClientAccount,
    toClientRecord,
    toContactEntry,
    fullName,
    durationSince,
    formatCalendarDate,
    formatContactTime,
    daysSince,
    sortContactsNewestFirst,
    recordPath,
} from "./helpers/client";
import {
    birthdaysInAWeek,
    birthdaysToday,
    soberAnniversariesInAWeek,
    soberAnniversariesToday,
} from "./helpers/milestones";
import { backTarget, useOpenedFrom } from "./helpers/openedFrom";

/** A recovery stat with no date behind it. */
const NOT_ON_FILE = "—";

/** What the profile's child routes — the overview and the full record — are handed. */
interface ClientOutletContext {
    client: ClientRecord;
}

/** The alum the surrounding profile route loaded. For the profile's child routes only. */
export const useClient = (): ClientRecord => useOutletContext<ClientOutletContext>().client;

/**
 * The profile route: loads the alum once, and renders the overview or the full record inside it —
 * so both share the loaded alum and the URL says which one is open.
 */
export function AlumniProfilePage() {
    const { t } = useTranslation();
    const { clientId = "" } = useParams();
    const { data, loading, error } = useQuery(ClientDocument, { variables: { clientId } });
    const foundClient = data?.client;

    if (!foundClient) {
        return (
            <div className="flex flex-col gap-4 px-4 py-8 md:px-10">
                <BackLink />
                {loading ? null : (
                    <Text muted>{t(error ? "admin.alumni.profile.loadFailed" : "admin.alumni.profile.notFound")}</Text>
                )}
            </div>
        );
    }
    const client = {
        ...toClientRecord(foundClient),
        account: toClientAccount(foundClient.user),
        contacts: foundClient.contacts.map(toContactEntry),
    };
    // Keyed so moving between alumni starts each one with its own notes.
    return <Outlet key={client.id} context={{ client } satisfies ClientOutletContext} />;
}

/** Back to the page that opened this profile — the one the browser's Back returns to — or the directory. */
function BackLink() {
    const { t } = useTranslation();
    const { to, labelKey } = backTarget(useOpenedFrom());
    return (
        <Link to={to} className="text-text-muted hover:text-text-heading inline-flex w-fit items-center gap-1 text-sm">
            <ChevronLeft className="h-4 w-4" aria-hidden />
            {t(`admin.alumni.nav.${labelKey}`)}
        </Link>
    );
}

/** The profile's index route: who the alum is, recent contacts and the newest notes. */
export function ClientProfileOverview() {
    const client = useClient();
    const { t } = useTranslation();
    const today = useMemo(() => new Date(), []);
    const {
        notes,
        loading: notesLoading,
        loadFailed: notesLoadFailed,
        addNote,
        editNote,
        deleteNote,
    } = useClientNotes(client.id);

    const name = fullName(client);
    const [
        logOpen,
        setLogOpen,
    ] = useState<boolean>(false);
    const [
        scheduleOpen,
        setScheduleOpen,
    ] = useState<boolean>(false);
    const [
        resettingSoberDate,
        setResettingSoberDate,
    ] = useState<boolean>(false);

    const saveContact = useLogContact();
    // Saved, then the profile is re-read, so the new contact comes back in the history from the API.
    const logContact = (contact: LoggedContact) => saveContact(contact, name);

    const saveFollowUp = useScheduleFollowUp();
    // Not a contact: the history is unchanged until someone actually reaches them.
    const scheduleFollowUp = (followUp: ScheduledFollowUp) => saveFollowUp(client.id, followUp, name);

    const personalDetails = [
        { label: t("admin.alumni.profile.sex"), value: client.sex },
        { label: t("admin.alumni.profile.genderIdentity"), value: client.genderIdentity },
        { label: t("admin.alumni.profile.address"), value: client.address },
    ].filter((field): field is { label: string; value: string } => Boolean(field.value));

    const lifeStatus = [
        { label: t("admin.alumni.profile.employment"), value: client.employment },
        { label: t("admin.alumni.profile.housing"), value: client.housing },
    ];

    const emergency = client.emergencyContact;
    const emergencyReach = [
        emergency?.relationship,
        emergency?.phone,
    ].filter(Boolean);

    return (
        <div className="flex flex-col gap-6 px-4 py-6 md:px-10">
            <BackLink />

            <header className="flex flex-col gap-5 lg:flex-row lg:items-start">
                <Avatar size="xl" className="shrink-0">
                    <AvatarGradientFallback>{initialsFor(client.firstName, client.lastName)}</AvatarGradientFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                        <Heading level={3} as="h1">
                            {name}
                        </Heading>
                        <ContactStatusBadge status={client.status} />
                        <DoNotContactSelect client={client} />
                        {birthdaysToday([client], today).length > 0 ? (
                            <MilestoneBadge kind="birthday" size="sm" />
                        ) : null}
                        {birthdaysInAWeek([client], today).length > 0 ? (
                            <MilestoneBadge kind="birthday" when="inAWeek" size="sm" />
                        ) : null}
                        {soberAnniversariesToday([client], today).map(({ years }) => (
                            <MilestoneBadge key="sober" kind="soberAnniversary" years={years} size="sm" />
                        ))}
                        {soberAnniversariesInAWeek([client], today).map(({ years }) => (
                            <MilestoneBadge
                                key="sober"
                                kind="soberAnniversary"
                                years={years}
                                when="inAWeek"
                                size="sm"
                            />
                        ))}
                    </div>
                    {client.statusReason ? (
                        <Text size="sm" muted className="mt-1">
                            {t("admin.alumni.profile.statusReason", { reason: client.statusReason })}
                        </Text>
                    ) : null}
                    {client.status === "DO_NOT_CONTACT" && client.doNotContactReason === "READMITTED" ? (
                        <Text size="sm" muted className="mt-1">
                            {t("admin.alumni.doNotContact.readmittedHint")}
                        </Text>
                    ) : null}
                    {client.lostContactSuggested ? (
                        <Text size="sm" muted className="mt-1">
                            {t("admin.alumni.lostContact.suggestion", {
                                count: LOST_CONTACT_SUGGESTED_AFTER_INTERVALS,
                                firstName: client.firstName,
                            })}
                        </Text>
                    ) : null}
                    <dl className="text-text-muted mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                        {client.phone ? (
                            <HeaderDetail
                                icon={<Phone className="h-3.5 w-3.5" />}
                                label={t("admin.alumni.profile.phone")}
                            >
                                {client.phone}
                            </HeaderDetail>
                        ) : null}
                        {client.email ? (
                            <HeaderDetail
                                icon={<Mail className="h-3.5 w-3.5" />}
                                label={t("admin.alumni.profile.email")}
                            >
                                {client.email}
                            </HeaderDetail>
                        ) : null}
                        {client.location ? (
                            <HeaderDetail
                                icon={<MapPin className="h-3.5 w-3.5" />}
                                label={t("admin.alumni.profile.location")}
                            >
                                {client.location}
                            </HeaderDetail>
                        ) : null}
                        {client.welcomeEmailSentAt ? (
                            <HeaderDetail
                                icon={<Mail className="h-3.5 w-3.5" />}
                                label={t("admin.alumni.welcome.send")}
                            >
                                {t("admin.alumni.welcome.sentOn", {
                                    date: formatContactTime(client.welcomeEmailSentAt),
                                })}
                            </HeaderDetail>
                        ) : null}
                        {client.coordinator ? (
                            <HeaderDetail
                                icon={<UserRound className="h-3.5 w-3.5" />}
                                label={t("admin.alumni.profile.coordinator")}
                            >
                                {client.coordinator}
                            </HeaderDetail>
                        ) : null}
                    </dl>
                </div>
                <div className="flex shrink-0 flex-row flex-wrap gap-2">
                    {client.lostContactSuggested ? <MarkLostContact client={client} /> : null}
                    {client.status === "LOST_CONTACT" ? <ReturnToOutreach client={client} /> : null}
                    <SendWelcomeEmail client={client} />
                    <Button
                        variant="outline"
                        className="w-auto"
                        leftIcon={<CalendarPlus className="h-4 w-4" />}
                        onClick={() => setScheduleOpen(true)}
                    >
                        {t("admin.alumni.profile.scheduleFollowUp")}
                    </Button>
                </div>
            </header>

            <div className="grid items-start gap-5 lg:grid-cols-[22rem_minmax(0,1fr)]">
                <div className="grid gap-5">
                    <Card width="full" className="p-5">
                        <Eyebrow>{t("admin.alumni.profile.recovery")}</Eyebrow>
                        <div className="flex flex-col items-center">
                            <div className="my-3 grid grid-cols-2 gap-4">
                                <Stat
                                    value={
                                        client.soberDate
                                            ? formatDuration(durationSince(client.soberDate, today), t)
                                            : NOT_ON_FILE
                                    }
                                    label={t("admin.alumni.profile.sober")}
                                    detail={
                                        client.soberDate
                                            ? t("admin.alumni.profile.since", {
                                                  date: formatCalendarDate(client.soberDate),
                                              })
                                            : t("admin.alumni.profile.noSoberDate")
                                    }
                                />
                                <Stat
                                    className="border-border border-l pl-4"
                                    value={
                                        client.dischargeDate
                                            ? String(daysSince(client.dischargeDate, today))
                                            : NOT_ON_FILE
                                    }
                                    label={t("admin.alumni.profile.daysPostDischarge")}
                                    detail={
                                        client.dischargeDate
                                            ? t("admin.alumni.profile.dischargedOn", {
                                                  date: formatCalendarDate(client.dischargeDate),
                                              })
                                            : t("admin.alumni.profile.stillInProgram")
                                    }
                                />
                            </div>
                            {resettingSoberDate ? (
                                <ResetSoberDate
                                    clientId={client.id}
                                    dischargeDate={client.dischargeDate}
                                    today={today}
                                    onCancel={() => setResettingSoberDate(false)}
                                    onSaved={() => setResettingSoberDate(false)}
                                />
                            ) : (
                                <Button variant="outline" onClick={() => setResettingSoberDate(true)}>
                                    {t("admin.alumni.profile.resetSoberDate.title")}
                                </Button>
                            )}
                        </div>
                        <dl className="border-border mt-5 grid gap-4 border-t pt-5">
                            {client.soberDate ? (
                                <Detail label={t("admin.alumni.profile.soberDate")}>
                                    {formatCalendarDate(client.soberDate)}
                                </Detail>
                            ) : null}
                            <Detail label={t("admin.alumni.profile.program")}>
                                {client.program ?? t("admin.alumni.profile.notOnFile")}
                            </Detail>
                            {client.dischargeType ? (
                                <Detail label={t("admin.alumni.profile.dischargeType")}>
                                    {t(`admin.alumni.dischargeType.${client.dischargeType}`)}
                                </Detail>
                            ) : null}
                            {client.birthday ? (
                                <Detail label={t("admin.alumni.profile.birthday")}>
                                    {formatCalendarDate(client.birthday)}
                                </Detail>
                            ) : null}
                        </dl>
                    </Card>

                    <Card width="full" className="p-5">
                        <Eyebrow>{t("admin.alumni.profile.lifeStatus")}</Eyebrow>
                        <dl className="mt-3 grid gap-4">
                            {lifeStatus.map((field) => (
                                <Detail key={field.label} label={field.label}>
                                    {field.value ?? t("admin.alumni.profile.notOnFile")}
                                </Detail>
                            ))}
                        </dl>
                    </Card>

                    {personalDetails.length > 0 ? (
                        <Card width="full" className="p-5">
                            <Eyebrow>{t("admin.alumni.profile.personalDetails")}</Eyebrow>
                            <dl className="mt-3 grid gap-4">
                                {personalDetails.map((field) => (
                                    <Detail key={field.label} label={field.label}>
                                        {field.value}
                                    </Detail>
                                ))}
                            </dl>
                        </Card>
                    ) : null}

                    <ContactDetailsCard client={client} />

                    <Card width="full" className="p-5">
                        <Eyebrow>{t("admin.alumni.profile.emergencyContact")}</Eyebrow>
                        {emergency ? (
                            <div className="mt-3">
                                <Text size="sm" weight="medium" className="text-text-heading">
                                    {emergency.name}
                                </Text>
                                {emergencyReach.length > 0 ? (
                                    <Text size="sm" muted className="mt-1">
                                        {emergencyReach.join(" · ")}
                                    </Text>
                                ) : null}
                            </div>
                        ) : (
                            <Text size="sm" muted className="mt-3">
                                {t("admin.alumni.profile.noEmergencyContact")}
                            </Text>
                        )}
                    </Card>
                </div>
                <div className="grid min-w-0 gap-5">
                    <ContactHistoryCard
                        contacts={sortContactsNewestFirst(client.contacts)}
                        onLogContact={() => setLogOpen(true)}
                        seeAllTo={recordPath(client.id, "contacts")}
                    />
                    <CoordinatorNotesCard
                        firstName={client.firstName}
                        notes={notes}
                        readAllTo={recordPath(client.id, "notes")}
                        loading={notesLoading}
                        loadFailed={notesLoadFailed}
                        onAdd={addNote}
                        onEdit={editNote}
                        onDelete={deleteNote}
                    />
                </div>
            </div>

            <LogContactDialog
                open={logOpen}
                onOpenChange={setLogOpen}
                client={{ id: client.id, name }}
                today={today}
                onLog={logContact}
            />
            <ScheduleFollowUpDialog
                open={scheduleOpen}
                onOpenChange={setScheduleOpen}
                clientName={name}
                today={today}
                onSchedule={scheduleFollowUp}
            />
        </div>
    );
}

type Translate = TFunction;

/** "7 mo 21 d" under a year, "1 yr 2 mo" after, "12 days" in the first month. */
const formatDuration = ({ years, months, days }: Duration, t: Translate): string => {
    if (years > 0) {
        return t("admin.alumni.profile.durationYears", { years, months });
    }
    if (months > 0) {
        return t("admin.alumni.profile.durationMonths", { months, days });
    }
    return t("admin.alumni.profile.durationDays", { count: days });
};

function HeaderDetail({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
    return (
        <div className="inline-flex items-center gap-1.5">
            <dt className="text-text-caption">
                <span aria-hidden>{icon}</span>
                <span className="sr-only">{label}</span>
            </dt>
            <dd>{children}</dd>
        </div>
    );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div>
            <dt>
                <Eyebrow as="span">{label}</Eyebrow>
            </dt>
            <dd className="text-text-heading mt-1 text-sm">{children}</dd>
        </div>
    );
}

function Stat({
    value,
    label,
    detail,
    className,
}: {
    value: string;
    label: string;
    detail: string;
    className?: string;
}) {
    return (
        <div className={className}>
            <p className="font-display text-text-heading text-2xl leading-tight font-semibold tracking-tight">
                {value}
            </p>
            <Text size="xs" muted className="mt-1">
                {label}
            </Text>
            <Text size="xs" className="text-text-caption mt-0.5 font-mono">
                {detail}
            </Text>
        </div>
    );
}
